import { AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChildren, ElementRef, EventEmitter, Input, OnDestroy, Output, QueryList, inject, HostBinding, ViewChild } from '@angular/core';
import { HisWizardPageComponent } from './his-wizard-page.component';
import { BlockableUI, MenuItem, MenuItemCommandEvent, MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { HisWizardFinishedEvent, HisWizardItem, HisWizardPageCloseEvent, HisWizardPageOpenEvent } from './his-wizard-interface';
import { OverlayPanel } from 'primeng/overlaypanel';

@Component( {
    selector: 'his-wizard',
    templateUrl: './his-wizard.component.html',
    styleUrls: ['./his-wizard.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
} )
export class HisWizardComponent implements BlockableUI, AfterContentInit, OnDestroy {
    el = inject( ElementRef );
    changeDetector = inject( ChangeDetectorRef );
    @ViewChild( 'mobileStepMenu' ) mobileStepMenu!: OverlayPanel;

    mobileMenuOpen = false;
    validPages: boolean[] = [];

    @Input() hideAccordion = true;

    /**
     * Is sidebar visible?
     * @group Props
     */
    @Input() sidebarVisible = true;
    /**
     * Whether to show a notification when dynamic pages are created.
     * @group Props
     */
    @Input() showNotification = false;
    /**
     * When enabled, multiple pages can be activated at the same time.
     * @group Props
     */
    // @Input() multiple = false;
    /**
     * Inline style of the tab header and content.
     * @group Props
     */
    @Input() style: { [klass: string]: any } | null | undefined;
    /**
     * Class of the element.
     * @group Props
     */
    @Input() styleClass: string | undefined;
    /**
     * Icon of a collapsed tab.
     * @group Props
     */
    //@Input() expandIcon: string | undefined;
    /**
     * Icon of an expanded tab.
     * @group Props
     */
    //@Input() collapseIcon: string | undefined;
    /**
     * Index of the active tab or an array of indexes in multiple mode.
     * @group Props
     */
    @Input() get activeIndex(): number | null | undefined {
        return this._activeIndex;
    }
    set activeIndex( val: number | null | undefined ) {
        this._activeIndex = val;
        if ( this.preventActiveIndexPropagation ) {
            this.preventActiveIndexPropagation = false;
            return;
        }

        this.updateSelectionState();
    }
    /**
     *
     */
    @Input() get activePage(): string | null | undefined {
        return this._activePage;
    }
    private set activePage( val: string | null | undefined ) {
        this._activePage = val;
        if ( this.preventActivePagePropagation ) {
            this.preventActivePagePropagation = false;
            return;
        }

        this.updateSelectionState();
    }
    /**
     * Callback to invoke when an active tab is collapsed by clicking on the header.
     * @param {HisWizardPageCloseEvent} event - Custom tab close event.
     * @group Emits
     */
    @Output() tabClose: EventEmitter<HisWizardPageCloseEvent> = new EventEmitter();
    /**
     * Callback to invoke when a tab gets expanded.
     * @param {HisWizardPageOpenEvent} event - Custom tab open event.
     * @group Emits
     */
    @Output() tabOpen: EventEmitter<HisWizardPageOpenEvent> = new EventEmitter();
    /**
     * Returns the active index.
     * @param {number | number[]} value - New index.
     * @group Emits
     */
    @Output() activeIndexChange: EventEmitter<number | number[]> = new EventEmitter<number | number[]>();

    /**
     * Callback to invoke when a tab gets expanded.
     * @param {HisWizardPageOpenEvent} event - Custom tab open event.
     * @group Emits
     */
    @Output() wizardFinished: EventEmitter<HisWizardFinishedEvent> = new EventEmitter();

    @ContentChildren( HisWizardPageComponent ) tabList: QueryList<HisWizardPageComponent> | undefined;

    tabListSubscription: Subscription | null = null;

    private _activeIndex: number | null | undefined;

    private _activePage: string | null | undefined;

    preventActiveIndexPropagation = false;

    preventActivePagePropagation = false;

    pages: HisWizardPageComponent[] = [];

    menuItems: MenuItem[] = [];

    wizardItems: HisWizardItem[] = [];

    ngAfterContentInit() {
        this.initTabs();
        // subscribe to tab changes
        this.tabListSubscription = ( this.tabList as QueryList<HisWizardPageComponent> ).changes.subscribe( ( _ ) => {
            this.initTabs();
        } );

        for ( let i = 0; i < this.pages.length; i++ ) {
            this.pages[i].statusChange.subscribe( ( event ) => {
                console.log( 'status changed index', event.index );
                console.log( 'status changed status', event.status );
                if ( event.status === 'VALID' ) this.validPages[event.index] = true;
                if ( event.status === 'INVALID' ) this.validPages[event.index] = false;
                if ( event === 'WIZARD_SUBMIT' ) {
                    this.wizardFinished.emit();
                }
                if ( event.status == 'SHOW_PAGE_KEY' ) {
                    console.log( 'wizard status: SHOW_PAGE_KEY' );
                    this.showPageKey( event.pageKey as string );
                }
                this.changeDetector.markForCheck();
            } );

            // Fix für visuelles Hervorheben der aktiven Wizard Seite im Menü, wenn
            // direkt auf Reiter statt über Next/Prev Button navigiert wird
            this.pages[i].pageSelected.subscribe( ( selectedIndex: number ) => {
                this.updateMenuItemsStyle( selectedIndex );
            } );
        }
    }

    initTabs() {
        this.pages = ( this.tabList as QueryList<HisWizardPageComponent> ).toArray();
        this.updateSelectionState();
        this.changeDetector.markForCheck();
        this.initItemModels();

    }

    initItemModels() {
        // Fix für das Duplizieren von Menüeinträgen
        this.menuItems = [];
        this.validPages = new Array( this.pages.length ).fill( false );
        // Ermittle den initial aktiven Index (default: 0)
        const maxActive = typeof this._activeIndex === 'number' ? this._activeIndex : 0;

        for ( let i = 0; i < this.pages.length; i++ ) {
            this.pages[i].index = i;
            // Alle Seiten bis einschließlich activeIndex aktivieren, Rest deaktivieren
            this.pages[i].disabled = i > maxActive;
            // if ( i != 0 ) {
            //     // disable tabs initially
            //     this.pages[i].disabled = true;
            // }
            let menuItem: MenuItem = {
                label: this.pages[i].title,
                disabled: this.pages[i].disabled,
                icon: 'iconfont-bullet_his_blue',
                // Visuelles Hervorheben der ersten Seite beim Wizard Menü Init
                styleClass: this.pages[i].selected ? 'his-wizard-menu-item-selected' : '',
                command: ( event: MenuItemCommandEvent ) => {
                    this.selectPageIndex( i );
                },
            };
            let wizardItem: HisWizardItem = {
                index: i,
                pageKey: this.pages[i].id
            };
            if ( this.pages[i].pageKey !== undefined ) {
                wizardItem.pageKey = this.pages[i].pageKey;
                console.log( 'pageKey: ' + this.pages[i].pageKey );
            }
            if ( this.pages[i].visible ) {
                this.menuItems.push( menuItem );
            }
            this.wizardItems.push( wizardItem );
            console.log( 'menuItems: ', menuItem );
        }
    }

    getBlockableElement(): HTMLElement {
        return this.el.nativeElement.children[0];
    }

    set selectPage( pageKey: string ) {
        for ( let item of this.wizardItems ) {
            if ( pageKey === item.pageKey ) {
                this.selectPageIndex( item.index );
            }
        }
    }
    get totalActiveSteps(): number {
        let count = 0;
        for ( let i = 0; i < this.wizardItems.length; i++ ) {
            if ( this.isPageVisible( i ) ) {
                count++;
            }
        }
        return count;
    }

    get currentStep(): number {
        if ( !this.wizardItems?.length ) return 0; // leer
        const idx = Math.min( Math.max( this.activeIndex ?? 0, 0 ), this.wizardItems.length - 1 ); // clamp
        let step = 0;
        for ( let i = 0; i <= idx; i++ ) {
            if ( this.isPageVisible( i ) ) step++; // nur sichtbare
        }
        return step;
    }

    get progressValue(): number {
        const t = this.totalActiveSteps || 1;
        return Math.round( ( this.currentStep / t ) * 100 ); // für Progressbar
    }
    selectPrevPage() {
        console.log( '_activeIndex', this._activeIndex );
        let index = this._activeIndex - 1;
        if ( this.isPageVisible( index ) && !this.isPageDisabled( index ) ) {
            this.selectPageIndex( index );
        } else {
            // select next visible active page
            for ( let i = index; 0 <= i; i-- ) {

                if ( this.isPageVisible( i ) && !this.isPageDisabled( i ) ) {
                    this.selectPageIndex( i );
                    break;
                }
            }
        }
    }
    selectNextPage() {
        console.log( '_activeIndex', this._activeIndex );
        let index = this._activeIndex + 1;
        if ( this.isPageVisible( index ) ) {
            this.enablePage( index );
            this.selectPageIndex( index );
        } else {
            //select next visible active page
            for ( let i = index; this.pages.length > i; i++ ) {

                if ( this.isPageVisible( i ) ) {
                    this.selectPageIndex( i );
                    break;
                }
            }
        }

        this.updateSelectionState();
    }

    isPageVisible( index: number ) {
        return !!this.pages?.[index]?.visible;
    }

    isPageDisabled( index: number ) {
        return !!this.pages?.[index]?.disabled;
    }

    isIndexValid( index: number ) {
        if ( index >= 0 && index < this.wizardItems.length && this.isPageVisible( index ) ) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Konvertiert einen pages-Index zu einem menuItems-Index
     * (berücksichtigt unsichtbare Pages)
     * @param pagesIndex Index im pages Array
     * @returns Index im menuItems Array
     */
    private convertPagesIndexToMenuIndex( pagesIndex: number ): number {
        let menuIndex = 0;
        for ( let i = 0; i < pagesIndex && i < this.pages.length; i++ ) {
            if ( this.pages[i].visible ) {
                menuIndex++;
            }
        }
        return menuIndex;
    }

    private disablePage( index: number ) {
        this.pages[index].disabled = true;
    }

    private enablePage( index: number ) {
        this.pages[index].disabled = false;

        const menuIndex = this.convertPagesIndexToMenuIndex( index );
        if ( this.menuItems[menuIndex] ) {
            this.menuItems[menuIndex].disabled = false;
        }
        this.changeDetector.markForCheck();
    }

    /**
     * disables a given page by pageKey
     * @param pageKey
     * @returns
     */
    disablePageKey( pageKey: string ) {

        if ( this.pages && this.pages.length ) {
            for ( let i = 0; i < this.pages.length; i++ ) {
                if ( this.pages[i].pageKey === pageKey ) {

                    this.pages[i].disabled = true;
                    return;
                }
            }
            console.warn( 'pageKey not available', pageKey );
        }
    }

    /**
     * enables a given page by pageKey
     * @param pageKey
     * @returns
     */
    enablePageKey( pageKey: string ) {

        if ( this.pages && this.pages.length ) {
            for ( let i = 0; i < this.pages.length; i++ ) {
                if ( this.pages[i].pageKey === pageKey ) {

                    this.pages[i].disabled = false;
                    return;
                }
            }
            console.warn( 'pageKey not available', pageKey );
        }
    }
    /**
     * hides and disables a page
     * @param pageKey pagekey of the page to hide
     * @returns void
     */
    hidePageKey( pageKey: string ) {
        if ( this.pages && this.pages.length ) {
            for ( let i = 0; i < this.pages.length; i++ ) {
                if ( this.pages[i].pageKey === pageKey ) {
                    this.pages[i].disabled = true;
                    this.pages[i].visible = false;
                    return;
                }
            }
            console.warn( 'pageKey not available', pageKey );
        }
    }

    /**
     * makes a page visible and enabled
     * @param pageKey
     * @returns
     */
    showPageKey( pageKey: string ) {
        console.warn( 'wizard show pageKey:', pageKey );
        if ( this.pages && this.pages.length ) {
            for ( let i = 0; i < this.pages.length; i++ ) {
                if ( this.pages[i].pageKey === pageKey ) {
                    const wasVisible = this.pages[i].visible;
                    this.pages[i].disabled = false;
                    this.pages[i].visible = true;
                    this.pages[i].changeDetector.markForCheck();
                    this.initItemModels();
                    if ( !wasVisible ) {
                        // Toast anzeigen
                        // this.messageService.add( {
                        //     severity: 'info',
                        //     summary: 'Neuer Wizard Schritt freigeschaltet ',
                        //     detail: `Schritt "${this.pages[i].title}" freigeschaltet`
                        // } );
                    }
                    return;
                }
            }
            console.warn( 'pageKey not available', pageKey );
        } else {
            console.warn( 'no pages available', pageKey );
        }
    }

    selectPageKey( pageKey: string ) {
        console.log( 'change pageKey to: ', pageKey );
        if ( this.pages && this.pages.length ) {
            for ( let i = 0; i < this.pages.length; i++ ) {
                if ( this.pages[i].pageKey === pageKey ) {
                    this.selectPageIndex( i );
                    return;
                }
            }
            console.warn( 'pageKey not available', pageKey );
        }
    }

    selectPageIndex( index: number ) {
        console.log( 'change index to: ', index );
        if ( this.pages[index].disabled ) {
            return;
        }
        this._activeIndex = index;
        if ( this.preventActiveIndexPropagation ) {
            this.preventActiveIndexPropagation = false;
            return;
        }

        this.updateSelectionState();
        this.mobileStepMenu?.hide();
    }

    private updateSelectionState() {
        if ( this.pages && this.pages.length && this._activeIndex != null ) {
            for ( let i = 0; i < this.pages.length; i++ ) {

                let selected = i === this._activeIndex;
                let changed = selected !== this.pages[i].selected;

                if ( changed ) {
                    this.pages[i].selected = selected;
                    this.pages[i].selectedChange.emit( selected );
                    this.pages[i].changeDetector.markForCheck();
                    this.updateMenuItemsStyle( this._activeIndex );
                }
            }
        }
    }

    // Visuelles Hervorheben der aktiven Seite im Menü anhand von Styling
    private updateMenuItemsStyle( index: number ) {
        const updatedMenuItems = [...this.menuItems];  // Erstelle eine neue Referenz

        // Alle Elemente zurücksetzen
        for ( let i = 0; i < updatedMenuItems.length; i++ ) {
            updatedMenuItems[i].styleClass = updatedMenuItems[i].styleClass.replace( 'his-wizard-menu-item-selected', '' ).trim();
        }

        // Konvertiere den pages-Index zu menuItems-Index (berücksichtige unsichtbare Pages)
        const menuIndex = this.convertPagesIndexToMenuIndex( index );

        // Das Element mit dem berechneten Index auswählen und markieren
        if ( updatedMenuItems[menuIndex] && !updatedMenuItems[menuIndex].styleClass.includes( 'his-wizard-menu-item-selected' ) ) {
            updatedMenuItems[menuIndex].styleClass += ' his-wizard-menu-item-selected';
        }

        this.menuItems = updatedMenuItems;  // Neue Referenz zuweisen
        this.changeDetector.markForCheck();
    }


    private updateActiveIndex() {
        let index: number | null;
        this.pages.forEach( ( page, i ) => {
            if ( page.selected ) {
                // if ( this.multiple ) {
                //     ( index as number[] ).push( i );
                // } else {
                index = i;
                return;
                // }
            }
        } );

        this.preventActiveIndexPropagation = true;
        this.activeIndexChange.emit( index as number );
    }

    @HostBinding( 'class.hide-accordion' )
    get _hideAccordionClass() {
        return this.hideAccordion;
    }

    toggleMobileMenu( event: Event ): void {
        this.mobileMenuOpen = !this.mobileMenuOpen;
        this.mobileStepMenu.toggle( event );
    }



    ngOnDestroy() {
        if ( this.tabListSubscription ) {
            this.tabListSubscription.unsubscribe();
        }
        if ( this.pages ) {
            this.pages.forEach( function( page ) {
                page.statusChange.unsubscribe();
            } );
        }
    }
}
